import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import ImageLightbox from "@/components/ImageLightbox";
import PackageReceipts from "@/components/PackageReceipts";
import { ArrowLeft, UploadSimple, ImageSquare, Truck, MapPin, Printer, MagnifyingGlassPlus, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { HsCodePicker } from "@/components/HsCodePicker";

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [notes, setNotes] = useState("");
  const [podBlobUrl, setPodBlobUrl] = useState(null);
  const [podLightboxOpen, setPodLightboxOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanForm, setScanForm] = useState({ branch_id: "", event_type: "scan", notes: "" });
  // Inline editor for the package's ASYCUDA HS code — toggled by the pencil
  // icon next to the Info row. Empty value clears the override.
  const [hsEditing, setHsEditing] = useState(false);
  const [hsDraft, setHsDraft] = useState("");
  const fileRef = useRef(null);
  const readOnly = user.role === "user" || user.role === "customer";

  const load = async () => {
    const { data } = await api.get(`/packages/${id}`);
    setPkg(data);
    setNotes(data.notes || "");
    if (data.pod_image_path) {
      // fetch as blob to preserve auth
      try {
        const resp = await api.get(`/files/${data.pod_image_path}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        setPodBlobUrl(url);
      } catch (e) {
        setPodBlobUrl(null);
      }
    } else {
      setPodBlobUrl(null);
    }
    // Hub trail + branches list
    try {
      const [ev, br] = await Promise.all([
        api.get(`/packages/${id}/events`),
        api.get(`/branches`),
      ]);
      setEvents(ev.data || []);
      setBranches(br.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    return () => { if (podBlobUrl) URL.revokeObjectURL(podBlobUrl); };
    // eslint-disable-next-line
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/packages/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/packages/${id}`, { notes });
      toast.success("Notes saved");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const saveHsCode = async () => {
    try {
      // Empty string clears the override (manifest-level HS code applies again)
      await api.patch(`/packages/${id}`, { hs_code: hsDraft || "" });
      toast.success(hsDraft ? "HS code saved" : "HS code cleared");
      setHsEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save HS code");
    }
  };

  const uploadPOD = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/packages/${id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Proof of delivery uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!pkg) {
    return (
      <div className="p-8 text-zinc-500" data-testid="loading">Loading package...</div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]" data-testid="package-detail">
      <Toaster position="top-right" />
      <button
        onClick={() => navigate(-1)}
        className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Tracking
          </div>
          <div className="flex items-center gap-3 mt-1">
            <code className="text-2xl font-mono font-semibold text-zinc-900">{pkg.tracking_number}</code>
            <StatusBadge status={pkg.status} />
          </div>
          <div className="mt-3 text-xs text-zinc-500">
            Created {new Date(pkg.created_at).toLocaleString()} · Updated {new Date(pkg.updated_at).toLocaleString()}
          </div>
        </div>

        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const resp = await api.get(`/packages/${id}/label`, { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const win = window.open(url, "_blank");
                  if (!win) toast.message("Pop-ups blocked — open from downloads");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (e) {
                  toast.error("Failed to open shipping label");
                }
              }}
              data-testid="print-label-btn"
            >
              <Printer size={14} weight="bold" className="mr-1.5" /> Print Label
            </Button>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Set status</Label>
            <Select value={pkg.status} onValueChange={updateStatus}>
              <SelectTrigger className="w-44" data-testid="set-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="ready_for_pickup">Ready for Pickup</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Parties */}
        <div className="bg-white border border-zinc-200 rounded-md p-5 lg:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-4">Route</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                <Truck size={14} /> SENDER
              </div>
              <div className="font-semibold text-zinc-900">{pkg.sender_name}</div>
              <div className="text-sm text-zinc-600 mt-1">{pkg.sender_address}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                <MapPin size={14} /> RECIPIENT
              </div>
              <div className="font-semibold text-zinc-900">{pkg.recipient_name}</div>
              <div className="text-sm text-zinc-600 mt-1">{pkg.recipient_address}</div>
              {pkg.recipient_phone && <div className="text-xs text-zinc-500 mt-1">{pkg.recipient_phone}</div>}
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-zinc-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Info
              label="Weight"
              value={(
                <div>
                  <div>{pkg.weight_kg} kg</div>
                  <div className="text-[11px] text-zinc-500 font-normal">{(pkg.weight_kg * 2.20462).toFixed(2)} lb</div>
                </div>
              )}
            />
            <Info label="Description" value={pkg.description || "—"} />
            <Info label="Dispatcher" value={pkg.assigned_dispatcher_id ? "Assigned" : "Unassigned"} />
            <div className="sm:col-span-3" data-testid="pkg-hs-code-row">
              {hsEditing && !readOnly ? (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                    ASYCUDA HS Code
                  </Label>
                  <HsCodePicker
                    value={hsDraft}
                    onChange={setHsDraft}
                    testId="pkg-detail-hs-picker"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={saveHsCode}
                      className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
                      data-testid="pkg-hs-save-btn"
                    >
                      <Check size={13} weight="bold" className="mr-1" /> Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setHsEditing(false); setHsDraft(pkg.hs_code || ""); }}
                      data-testid="pkg-hs-cancel-btn"
                    >
                      <X size={13} weight="bold" className="mr-1" /> Cancel
                    </Button>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-2">
                    Overrides the manifest-wide HS code at customs export. Leave empty to fall back to the manifest default.
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <Info
                    label="ASYCUDA HS Code"
                    value={
                      pkg.hs_code ? (
                        <span className="font-mono font-semibold text-zinc-900" data-testid="pkg-hs-code-value">
                          {pkg.hs_code}
                        </span>
                      ) : (
                        <span className="text-zinc-500 text-xs">— (uses manifest default)</span>
                      )
                    }
                  />
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setHsDraft(pkg.hs_code || ""); setHsEditing(true); }}
                      className="text-zinc-600 hover:text-[#002BF6] shrink-0 mt-5"
                      data-testid="pkg-hs-edit-btn"
                    >
                      <PencilSimple size={13} weight="bold" className="mr-1" />
                      {pkg.hs_code ? "Change" : "Set"}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Info
              label="Manifest"
              value={pkg.manifest_id ? (
                <Link to={`/manifests/${pkg.manifest_id}`} className="text-[#002BF6] hover:text-[#0021C7] font-semibold" data-testid="pkg-manifest-link">
                  View manifest 
                </Link>
              ) : "Not assigned"}
            />
            {pkg.route_id && (
              <Info
                label="Route zone"
                value={
                  <Link
                    to="/routes"
                    className="inline-flex items-center gap-1.5 font-semibold hover:underline"
                    style={{ color: pkg.route_color || "#002BF6" }}
                    data-testid="pkg-route-link"
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: pkg.route_color || "#002BF6" }} />
                    {pkg.route_name || "View route"}
                  </Link>
                }
              />
            )}
          </div>

          {/* Driver assignment (Ground only) */}
          {pkg.shipment_method === "ground" && !readOnly && (
            <DriverAssignmentCard pkg={pkg} reload={load} />
          )}

          {/* Notes */}
          <div className="mt-6 pt-4 border-t border-zinc-100">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-2"
              placeholder={readOnly ? "No notes" : "Add delivery notes..."}
              disabled={readOnly}
              data-testid="notes-textarea"
            />
            {!readOnly && (
              <Button size="sm" onClick={saveNotes} className="mt-2 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-notes-btn">
                Save notes
              </Button>
            )}
          </div>
        </div>

        {/* Delivery attempts history — shown for any package with at least one attempt.
            Visible to super_admin / company_admin / dispatcher (readOnly-excluded). */}
        {!readOnly && Array.isArray(pkg?.delivery_attempts) && pkg.delivery_attempts.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="attempt-history">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                Delivery Attempts ({pkg.delivery_attempts.length})
              </div>
              {pkg.signature_required && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                  Signature required
                </span>
              )}
            </div>
            <ol className="space-y-2.5">
              {pkg.delivery_attempts.map((a, idx) => (
                <li key={idx} className="flex items-start gap-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2.5" data-testid={`attempt-${idx + 1}`}>
                  <div className="shrink-0 w-7 h-7 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold text-zinc-900 capitalize">
                        {(a.outcome || "").replace(/_/g, " ")}
                      </span>
                      <span className="text-[11px] text-zinc-500">
                        {a.attempted_at ? new Date(a.attempted_at).toLocaleString() : "—"}
                      </span>
                    </div>
                    {a.driver_name && (
                      <div className="text-[11px] text-zinc-500">Driver: <b>{a.driver_name}</b></div>
                    )}
                    {a.notes && (
                      <div className="text-xs text-zinc-700 mt-1 italic">"{a.notes}"</div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            {pkg.status === "return_to_sender" && (
              <div className="mt-3 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                Two attempts exhausted — package is being returned to sender.
              </div>
            )}
          </div>
        )}

        {/* Signature image — visible when captured */}
        {!readOnly && pkg?.signature_required && (pkg?.signature_image_path || pkg?.signed_by_name) && (
          <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="signature-block">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-3">Signature on Delivery</div>
            {pkg.signed_by_name && (
              <div className="text-sm mb-2">
                <span className="text-zinc-500">Signed by: </span>
                <b className="text-zinc-900" data-testid="signed-by-display">{pkg.signed_by_name}</b>
                {pkg.signed_at && <span className="text-[11px] text-zinc-500 ml-2">· {new Date(pkg.signed_at).toLocaleString()}</span>}
              </div>
            )}
            {pkg.signature_image_path && (
              <div className="rounded-md border border-zinc-200 bg-white p-2 inline-block">
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">Captured signature stored ✓</span>
              </div>
            )}
          </div>
        )}
        <div className="bg-white border border-zinc-200 rounded-md p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-4">Proof of Delivery</div>

          {podBlobUrl ? (
            <div>
              <button
                type="button"
                onClick={() => setPodLightboxOpen(true)}
                className="group relative w-full block rounded-md border border-zinc-200 overflow-hidden hover:border-[#002BF6] transition-colors"
                data-testid="pod-image-zoom-trigger"
                title="Click to enlarge"
              >
                <img src={podBlobUrl} alt="Proof of delivery" className="w-full block" data-testid="pod-image" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                    <MagnifyingGlassPlus size={20} weight="bold" className="text-[#002BF6]" />
                  </div>
                </div>
              </button>
              <div className="text-xs text-zinc-500 mt-2">Uploaded proof of delivery · click to enlarge</div>
            </div>
          ) : (
            <div className="border border-dashed border-zinc-300 rounded-md py-12 flex flex-col items-center justify-center text-zinc-400" data-testid="pod-empty">
              <ImageSquare size={32} />
              <div className="text-sm mt-2">No POD uploaded yet</div>
            </div>
          )}

          {!readOnly && (
            <>
              <input
                type="file"
                accept="image/*"
                ref={fileRef}
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadPOD(e.target.files[0])}
                data-testid="pod-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                className="w-full mt-4 bg-[#002BF6] hover:bg-[#0021C7] text-white"
                data-testid="upload-pod-btn"
              >
                <UploadSimple size={16} className="mr-1.5" weight="bold" />
                {podBlobUrl ? "Replace POD" : "Upload POD"}
              </Button>
              <div className="text-[11px] text-zinc-500 mt-2 text-center">
                Uploading marks package as Delivered.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Proof of Purchase Receipts (Customs valuation) */}
      <PackageReceipts packageId={id} user={user} onActivity={load} />

      {/* Hub trail (#10) */}
      <div className="bg-white border border-zinc-200 rounded-md p-5 mt-4" data-testid="hub-trail">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Hub Trail</div>
          {!readOnly && (
            <Dialog open={scanOpen} onOpenChange={setScanOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="scan-package-btn">
                  <MapPin size={14} weight="bold" className="mr-1.5" /> Log Scan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md" data-testid="scan-dialog">
                <DialogHeader><DialogTitle>Log Hub Scan</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Branch / Hub</Label>
                    <Select value={scanForm.branch_id} onValueChange={(v) => setScanForm({ ...scanForm, branch_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="scan-branch"><SelectValue placeholder="Select a branch (optional)" /></SelectTrigger>
                      <SelectContent>
                        {branches.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-zinc-500">No branches yet. Create one on the Branches page.</div>
                        ) : branches.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}{b.is_hub ? " (Hub)" : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Event type</Label>
                    <Select value={scanForm.event_type} onValueChange={(v) => setScanForm({ ...scanForm, event_type: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="scan-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scan">Hub scan / arrived</SelectItem>
                        <SelectItem value="checkpoint">Checkpoint passed</SelectItem>
                        <SelectItem value="out_for_delivery">Out for delivery</SelectItem>
                        <SelectItem value="exception">Exception / hold</SelectItem>
                        <SelectItem value="note">Note</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Notes (optional)</Label>
                    <Textarea
                      className="mt-1.5"
                      rows={2}
                      value={scanForm.notes}
                      onChange={(e) => setScanForm({ ...scanForm, notes: e.target.value })}
                      data-testid="scan-notes"
                      placeholder="e.g. Loaded on truck KGN-42"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setScanOpen(false)}>Cancel</Button>
                  <Button
                    className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
                    data-testid="save-scan-btn"
                    onClick={async () => {
                      try {
                        const payload = { event_type: scanForm.event_type, notes: scanForm.notes || undefined };
                        if (scanForm.branch_id) payload.branch_id = scanForm.branch_id;
                        await api.post(`/packages/${id}/scan`, payload);
                        toast.success("Scan logged");
                        setScanOpen(false);
                        setScanForm({ branch_id: "", event_type: "scan", notes: "" });
                        load();
                      } catch (e) {
                        toast.error(formatApiErrorDetail(e.response?.data?.detail));
                      }
                    }}
                  >
                    Log
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {events.length === 0 ? (
          <div className="py-8 text-center text-sm text-zinc-500" data-testid="hub-trail-empty">
            No hub scans yet. Logging scans creates a trail for last-mile drivers.
          </div>
        ) : (
          <ol className="relative border-l-2 border-zinc-100 ml-2 pl-5 space-y-4" data-testid="hub-trail-list">
            {events.map((ev) => {
              const c = EVENT_COLORS[ev.event_type] || EVENT_COLORS.scan;
              return (
                <li key={ev.id} className="relative" data-testid={`hub-event-${ev.id}`}>
                  <span
                    className={`absolute -left-[27px] top-1 w-3 h-3 rounded-full ${c.dot} border-2 border-white shadow`}
                    data-testid={`hub-event-dot-${ev.event_type}`}
                  ></span>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${c.badge}`}
                      data-testid={`hub-event-type-${ev.event_type}`}
                    >
                      {EVENT_LABELS[ev.event_type] || ev.event_type}
                    </span>
                    {ev.branch_name && (
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-700 bg-zinc-50 px-2 py-0.5 rounded-full border border-zinc-200">
                        <MapPin size={10} weight="bold" /> {ev.branch_name}
                      </span>
                    )}
                  </div>
                  {ev.notes && <div className="text-xs text-zinc-600 mt-1">{ev.notes}</div>}
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {new Date(ev.created_at).toLocaleString()}
                    {ev.actor_name ? ` · ${ev.actor_name}` : ""}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <ImageLightbox
        open={podLightboxOpen}
        onClose={() => setPodLightboxOpen(false)}
        src={podBlobUrl}
        alt="Proof of delivery"
        title={`Proof of delivery · ${pkg?.tracking_number || ""}`}
        testid="pod-lightbox"
      />
    </div>
  );
}

const EVENT_LABELS = {
  scan: "Hub scan",
  checkpoint: "Checkpoint",
  out_for_delivery: "Out for delivery",
  exception: "Exception",
  note: "Note",
};

// Per-event-type color palette. `dot` is the timeline marker; `badge` styles
// the inline label so the type is readable at a glance.
const EVENT_COLORS = {
  scan: {
    dot: "bg-[#002BF6]",
    badge: "bg-[#002BF6]/10 text-[#002BF6] border-[#002BF6]/20",
  },
  checkpoint: {
    dot: "bg-violet-600",
    badge: "bg-violet-50 text-violet-700 border-violet-200",
  },
  out_for_delivery: {
    dot: "bg-amber-500",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
  },
  exception: {
    dot: "bg-red-600",
    badge: "bg-red-50 text-red-700 border-red-200",
  },
  note: {
    dot: "bg-zinc-400",
    badge: "bg-zinc-100 text-zinc-700 border-zinc-200",
  },
};

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900 mt-1 font-medium">{value}</div>
    </div>
  );
}


function DriverAssignmentCard({ pkg, reload }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [drivers, setDrivers] = React.useState([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: sug }, { data: all }] = await Promise.all([
          api.get("/drivers/suggest", { params: { package_id: pkg.id } }),
          api.get("/drivers", { params: { active_only: true, pickers_only: true, package_weight_kg: pkg.weight_kg || undefined } }),
        ]);
        if (cancelled) return;
        setSuggestions(sug.suggestions || []);
        setDrivers(all || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [pkg.id]);

  const current = drivers.find((d) => d.id === pkg.driver_id);

  const assign = async (driver_id) => {
    setLoading(true);
    try {
      await api.patch(`/packages/${pkg.id}/driver`, { driver_id });
      toast.success(driver_id ? "Driver assigned" : "Driver unassigned");
      setPickerOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 pt-4 border-t border-zinc-100" data-testid="driver-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
            Delivery driver
          </div>
          {current ? (
            <div className="mt-2">
              <div className="text-sm font-semibold text-zinc-900" data-testid="assigned-driver-name">
                <Truck size={13} weight="bold" className="inline mr-1.5 text-emerald-600" />
                {current.name}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {current.vehicle_type} · {current.phone || "no phone"} · {current.active_package_count} active
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-500 mt-2" data-testid="no-driver-assigned">
              No driver assigned. {suggestions.length > 0 && "Suggestions ranked by distance below."}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {current ? (
            <Button size="sm" variant="outline" onClick={() => assign(null)} disabled={loading} data-testid="unassign-driver-btn">
              Unassign
            </Button>
          ) : null}
          <Button size="sm" onClick={() => setPickerOpen(true)} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="change-driver-btn">
            {current ? "Reassign" : "Assign driver"}
          </Button>
        </div>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg" data-testid="driver-picker-dialog">
          <DialogHeader>
            <DialogTitle>Pick a driver</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {suggestions.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">
                No active drivers. Add one in the Drivers section.
              </div>
            ) : (
              suggestions.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => assign(s.id)}
                  disabled={loading}
                  data-testid={`driver-option-${s.id}`}
                  className="w-full text-left border border-zinc-200 hover:border-[#002BF6] hover:bg-blue-50/40 rounded-md p-3 transition-colors"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                        {i === 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Best match</span>}
                        {s.name}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {s.vehicle_type} · {s.base_address || "no base"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-semibold text-zinc-900 font-mono">
                        {s.distance_km != null && s.distance_km < 999
                          ? `${s.distance_km.toFixed(1)} km`
                          : s.distance_km === 999
                            ? "same area"
                            : "—"}
                      </div>
                      <div className="text-[10px] text-zinc-500">load: {s.active_package_count}</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
